import React, { useState, KeyboardEvent } from 'react';
import { Select } from 'antd';
import { useGetProductCollections } from '@affiliates/hooks';
import { GetAdvertiserProductCollections_advertiser_shopifyResources_collections } from '@affiliates/queries/types/GetAdvertiserProductCollections';
import { cn } from '@frontend/shadcn/lib/utils';
import { useGetConnectedShopifyQuery } from '@frontend/app/hooks/useGetConnectedShopifyQuery';
import getDarkHexCodeFromString from '@frontend/app/utils/getDeteministicColor';
import { CollectionConditionInput } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { Input } from '@frontend/shadcn/components/ui/input';
import { XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { ClientForSync } from '../../../OfferForm';
import { extractNumericGId } from '../../PayoutRulesDrawer';

interface Collection {
  id: string;
  name: string;
  advertiserId?: number;
}

// Manual collection input component
const ManualCollectionInput: React.FC<{
  value?: Collection[];
  onChange: (collections: CollectionConditionInput[]) => void;
  className?: string;
  status?: 'error' | '';
}> = ({ value = [], onChange, className, status }) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string>('');

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();

      // Check for duplicate collection ID
      const isDuplicate = value.some((collection) => collection.id === inputValue.trim());

      if (isDuplicate) {
        setDuplicateError('This collection ID already exists');
        return;
      }

      const newCollection = {
        id: inputValue.trim(),
        name: 'NON_SHOPIFY_COLLECTION_ID',
      };
      onChange([...value, newCollection]);
      setInputValue('');
      setDuplicateError('');
    }
  };

  const handleRemoveCollection = (collectionId: string) => {
    const updatedCollections = value.filter((c) => c.id !== collectionId);
    onChange(updatedCollections);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-1 border rounded-lg bg-white transition-all duration-200',
        isFocused && 'border-primary ring-0 ring-primary',
        (status === 'error' || duplicateError) && 'border-red-500 focus-within:border-red-500 ring-0 ring-red-400',
        className,
      )}
    >
      <div className="flex flex-col w-full">
        <div className="flex items-center gap-1 flex-wrap flex-1 min-h-[20px]">
          {value.map((collection) => (
            <div key={collection.id} className="flex items-center gap-1 px-2 py-1 bg-grey-1 rounded-md text-sm">
              <span className="text-grey-6">{extractNumericGId(collection.id)}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveCollection(collection.id);
                }}
              >
                <XmarkIcon className="h-3 w-3 text-grey-4" />
              </Button>
            </div>
          ))}
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setDuplicateError('');
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={value.length === 0 ? 'Enter Collection IDs' : ''}
            className={cn(
              'flex-1 min-w-[120px] border-0 focus-visible:ring-0 p-0 h-6',
              (status === 'error' || duplicateError) && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
        </div>
        {duplicateError && <p className="text-red-500 text-xs mt-1">{duplicateError}</p>}
      </div>
    </div>
  );
};

interface PayoutCollectionConditionsProps {
  value?: Collection[];
  onChange: (collections: CollectionConditionInput[]) => void;
  className?: string;
  status?: 'error' | '';
  clientsForSync: ClientForSync[];
  usedStores: string[];
  selectedStoreId: string;
  onStoreSelect: (storeLabel: string) => void;
  isShopifyConnected: boolean;
}

export const PayoutCollectionConditions: React.FC<PayoutCollectionConditionsProps> = ({
  value,
  onChange,
  className,
  status,
  clientsForSync,
  usedStores,
  selectedStoreId,
  onStoreSelect,
  isShopifyConnected
}) => {
  const { data: connectedShopifyData } = useGetConnectedShopifyQuery();
  const [isMultipleShopifyConnected, setIsMultipleShopifyConnected] = React.useState(false);

  // Prepare store options with advertiserIds
  const storeOptions = React.useMemo(() => {
    if (!connectedShopifyData?.clientConnections || !connectedShopifyData?.clientConnections?.length || !clientsForSync) {
      setIsMultipleShopifyConnected(false);
      return [];
    }
    setIsMultipleShopifyConnected(true);

    return connectedShopifyData.clientConnections
      .filter((connection) =>
        clientsForSync.some((client) => client.advertiserId === connection.connectedAdvertiserId) ||
        connection.isPrimary
      )
      .map((connection) => ({
        label: connection.label,
        isPrimary: connection.isPrimary,
        advertiserId: connection.connectedAdvertiserId,
      }));
  }, [connectedShopifyData, clientsForSync]);

  // Find selected store by either label or advertiserId
  const selectedStore = React.useMemo(() => {
    if (!selectedStoreId) return undefined;

    // First try to find by label
    const storeByLabel = storeOptions.find((store) => store.label === selectedStoreId);
    if (storeByLabel) return storeByLabel;

    // If not found, try to find by advertiserId
    return storeOptions.find((store) => store.advertiserId.toString() === selectedStoreId);
  }, [storeOptions, selectedStoreId]);

  // Auto-select primary store if it's the only store
  React.useEffect(() => {
    if (storeOptions.length === 1 && !selectedStoreId) {
      onStoreSelect(storeOptions[0].label);
    }
  }, [storeOptions, selectedStoreId, onStoreSelect]);

  const collectionsQuery = useGetProductCollections({
    advertiserId: selectedStore?.advertiserId || null
  });

  const productCollections = (collectionsQuery.data || []) as GetAdvertiserProductCollections_advertiser_shopifyResources_collections[];

  const handleCollectionChange = (values: string[]) => {
    const selectedCollections = values.map((id) => {
      const collection = productCollections.find((c) => c.id.toString() === id);
      return {
        id: id.toString(),
        name: collection?.title || '',
        advertiserId: selectedStore?.advertiserId || null,
      };
    });
    onChange(selectedCollections);
  };

  if (!isShopifyConnected) {
    return <ManualCollectionInput value={value} onChange={onChange} className={className} status={status} />;
  }

  return (
    <div className="flex gap-2 w-full items-center">
      {storeOptions.length > 1 && (
        <div className="w-[30%]">
          <Select
            size="middle"
            placeholder="Select Store"
            style={{ width: '100%' }}
            onChange={onStoreSelect}
            value={selectedStore?.label}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ width: 'fit-content' }}
            popupClassName="store-selector-dropdown"
          >
            {storeOptions.map((store) => (
              <Select.Option
                key={store.advertiserId}
                value={store.label}
                disabled={usedStores?.includes(store.advertiserId.toString()) &&
                         selectedStore?.advertiserId.toString() !== store.advertiserId.toString()}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: getDarkHexCodeFromString(`${store.label}storeName`)
                    }}
                  />
                  <span className="whitespace-normal">{store.label}</span>
                  {store.isPrimary && (
                    <span className="text-xs text-[#167CF4] whitespace-nowrap">(Primary)</span>
                  )}
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>
      )}
      <div className={cn(storeOptions.length > 1 ? 'w-[70%]' : 'w-full')}>
        <Select
          size="middle"
          mode="multiple"
          showSearch
          filterOption={(input: string, option: { value: string, children: string }) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())}
          value={value?.map((c) => extractNumericGId(c.id))}
          onChange={handleCollectionChange}
          placeholder={isMultipleShopifyConnected && !selectedStore ? 'Please select a store first' : 'Select collections'}
          style={{ width: '100%' }}
          loading={collectionsQuery.loading}
          status={status}
          className={cn(className)}
          disabled={isMultipleShopifyConnected && !selectedStore?.advertiserId}
        >
          {productCollections.map((collection) => (
            <Select.Option
              key={collection.id}
              value={collection.id.toString()}
            >
              {collection.title}
            </Select.Option>
          ))}
        </Select>
      </div>
    </div>
  );
};
